import DaySwitch from '../../DaySwitch';
import Divider from '../../Divider';
import NumField from '../../NumField';
import Radio from '../../Radio';
import SimpleTable from '../../SimpeTable';
import {
  IFormState,
  Occupancy,
  defaultRoomOccupancy,
  formatRoomRequirementsSummary,
  normalizeRoomOccupancy,
  parseDateOnly,
  TECH_COUNT_MAX,
  MIN_ROOM_AREA_SQM_MAX,
  OTHER_SEATING,
  applyRoomSeating,
  applyRoomSeatingDescription,
} from '../../../semshared/utils/lister';
import { addDays } from 'date-fns/fp';
import { CapsuleTabContentBody, CapsuleTabContentHeader, ContentRow } from '../CapsuleComponents';
import Dropdown from '../../DropDown';
import AddRemoveButton from '../../AddRemoveButton';
import { AvailabilityType } from '../../../semshared/availability/availability';
import TimeField from '../../TimePicker';
import { LinkButton } from '../../LinkButton';
import Icon from '../../Icon';
import React, { useEffect, useState } from 'react';
import Checkbox from '../../Checkbox';
import Button from '../../Button';
import { useTranslation } from 'react-i18next';
import OtherSeatingDialog, { OtherSeatingIcon } from '../../OtherSeatingDialog';

interface MeetingRoomsSectionProps {
  formState: IFormState;
  requestFormStateUpdateUserInitiated: (newState: IFormState) => void;
  requestFormStateUpdateAutomatic: (newState: IFormState) => void;
  meetingRoomsDay: any;
  setMeetingRoomsDay: any;
  shadowRoot: any;
  availabilityChecker: AvailabilityType | null;
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
  pendingOtherSeating?: { dayIndex: number; roomIndex: number } | null;
  onPendingOtherSeatingHandled?: () => void;
}

/**
 * Drawer variant of meeting-room editing with detailed per-room controls.
 * @param props - Day context, manual/automatic update channels, and form state.
 * @returns Full meeting-room editor for advanced workflows.
 */
const MeetingRoomsSectionDrawer = ({
  formState,
  requestFormStateUpdateUserInitiated,
  requestFormStateUpdateAutomatic,
  meetingRoomsDay,
  setMeetingRoomsDay,
  shadowRoot,
  availabilityChecker,
  setPopupShow,
  pendingOtherSeating,
  onPendingOtherSeatingHandled,
}: MeetingRoomsSectionProps) => {
  const { t, i18n } = useTranslation();
  const [openRequirements, setOpenRequirements] = useState<Record<string, boolean>>({});
  const [otherEditorRoom, setOtherEditorRoom] = React.useState<number | null>(null);

  const patchRoomOccupancy = (roomIndex: number, patch: Partial<Occupancy>) => {
    requestFormStateUpdateUserInitiated({
      ...formState,
      days: formState.days.map((d, i) => {
        if (i !== meetingRoomsDay) return d;
        return {
          ...d,
          occupancy: d.occupancy.map((occ, j) => (j === roomIndex ? { ...normalizeRoomOccupancy(occ), ...patch } : occ)),
        };
      }),
    });
  };

  useEffect(() => {
    if (!pendingOtherSeating || pendingOtherSeating.dayIndex !== meetingRoomsDay) return;
    setOtherEditorRoom(pendingOtherSeating.roomIndex);
    onPendingOtherSeatingHandled?.();
  }, [pendingOtherSeating, meetingRoomsDay, onPendingOtherSeatingHandled]);

  const seatingOptions = [
    {
      value: 'UFORM',
      label: t('widget:labels.uform'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/U-form.png`} />,
    },
    {
      value: 'THEATER',
      label: t('widget:seating.theater'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/theater.png`} />,
    },
    {
      value: 'PARLAMENT',
      label: t('widget:seating.parliament'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/parlament.png`} />,
    },
    {
      value: 'CIRCLE',
      label: t('widget:seating.circle'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/kreis.png`} />,
    },
    {
      value: 'BANKETT',
      label: t('widget:seating.banquet'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/bankett.png`} />,
    },
    {
      value: 'COCKTAIL',
      label: t('widget:seating.cocktail'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/cocktail.png`} />,
    },
    {
      value: 'BLOCK',
      label: t('widget:seating.block'),
      icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/block.png`} />,
    },
    {
      value: OTHER_SEATING,
      label: t('widget:seating.other'),
      icon: <OtherSeatingIcon />,
    },
  ];

  useEffect(() => {
    // Check if any day has been manually edited
    const isAnyDayManuallyEdited = formState.days.some(day => day.seatCountsManuallyEdited === true);

    // Manual edits disable auto redistribution globally to avoid overriding user seat allocations.
    if (isAnyDayManuallyEdited) {
      return;
    }

    // Update seatCount for all days, not just the currently selected day
    const updatedDays = formState.days.map(day => {
      // If seat counts have been manually edited for this day, skip it
      if (day.seatCountsManuallyEdited === true) {
        return day;
      }

      const totalGuestsForDay = day.totalGuests || 0;
      const numberOfRooms = day.occupancy?.length || 0;

      let newCalculatedSeatCount: number[];

      if (numberOfRooms === 0) {
        newCalculatedSeatCount = [];
      } else {
        newCalculatedSeatCount = Array(numberOfRooms).fill(0);
        if (totalGuestsForDay > 0) {
          // First room acts as canonical bucket to keep total capacity clearly represented.
          newCalculatedSeatCount[0] = totalGuestsForDay; // First room gets all participants

          if (numberOfRooms > 1) {
            // For subsequent rooms, distribute based on totalGuests / numberOfRooms (rounded up)
            const guestsPerRemainingRoom = Math.ceil(totalGuestsForDay / numberOfRooms);
            for (let i = 1; i < numberOfRooms; i++) {
              newCalculatedSeatCount[i] = guestsPerRemainingRoom;
            }
          }
        }
      }

      // Check if an update is actually needed
      const existingSeatCount = day.seatCount || [];
      const needsUpdate =
        existingSeatCount.length !== newCalculatedSeatCount.length || existingSeatCount.some((sc, idx) => sc !== newCalculatedSeatCount[idx]);

      if (needsUpdate) {
        return {
          ...day,
          seatCount: newCalculatedSeatCount,
          seatCountsManuallyEdited: false,
        };
      }

      return day;
    });

    // Check if any day actually needs an update
    const hasUpdates = updatedDays.some((day, index) => {
      const originalDay = formState.days[index];
      const originalSeatCount = originalDay.seatCount || [];
      const newSeatCount = day.seatCount || [];
      return originalSeatCount.length !== newSeatCount.length || originalSeatCount.some((sc, idx) => sc !== newSeatCount[idx]);
    });

    if (hasUpdates) {
      requestFormStateUpdateAutomatic({
        ...formState,
        days: updatedDays,
      });
    }
  }, [
    // Watch all days by serializing the relevant properties
    JSON.stringify(
      formState.days.map(d => ({
        totalGuests: d.totalGuests,
        occupancyLength: d.occupancy?.length,
        seatCount: d.seatCount,
        seatCountsManuallyEdited: d.seatCountsManuallyEdited,
      })),
    ),
    // requestFormStateUpdateAutomatic is assumed to be stable
  ]);

  return (
    <>
      <CapsuleTabContentHeader>
        <ContentRow>
          <DaySwitch
            value={meetingRoomsDay}
            max={formState.days.length - 1}
            onChange={v => setMeetingRoomsDay(v)}
            date={addDays(meetingRoomsDay, parseDateOnly(formState.start))}
          />
        </ContentRow>
      </CapsuleTabContentHeader>
      {formState.days[meetingRoomsDay].occupancy.map((m, index) => (
        <div key={index} style={{ backgroundColor: 'white', padding: 15, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <strong style={{ display: 'block', whiteSpace: 'nowrap' }}>{t('widget:table.room') + ' ' + (index + 1)}</strong>
            <button
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {
                const isAnyDayManuallyEditedGlobal = formState.days.some(
                  d => d.seatCountsManuallyEdited === true && d !== formState.days[meetingRoomsDay],
                ); // Check if ANY OTHER day is manually edited

                requestFormStateUpdateUserInitiated({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      const currentTotalGuests = d.totalGuests || 0;
                      const updatedOccupancy = d.occupancy.filter((_, roomIndex) => roomIndex !== index);
                      const updatedSeating = d.seating.filter((_, roomIndex) => roomIndex !== index);
                      const updatedSeatingDescription = (d.seatingDescription || []).filter((_, roomIndex) => roomIndex !== index);
                      let recalculatedSeatCount: number[] = [];
                      const newTotalNumRooms = updatedOccupancy.length;

                      // Determine if this day should operate in manual mode for seat counts.
                      // It's manual if global state (another day) is manual, OR if this day itself was already marked manual.
                      const dayShouldBeManuallyHandled = isAnyDayManuallyEditedGlobal || d.seatCountsManuallyEdited;

                      if (dayShouldBeManuallyHandled) {
                        // Manual mode: preserve other counts, remove the specific one
                        recalculatedSeatCount = d.seatCount.filter((_, roomIndex) => roomIndex !== index);
                      } else {
                        // Automatic mode: redistribute
                        if (newTotalNumRooms > 0) {
                          recalculatedSeatCount = Array(newTotalNumRooms).fill(0);
                          if (currentTotalGuests > 0) {
                            recalculatedSeatCount[0] = currentTotalGuests;
                            if (newTotalNumRooms > 1) {
                              const guestsPerRemainingRoom = Math.ceil(currentTotalGuests / newTotalNumRooms);
                              for (let j = 1; j < newTotalNumRooms; j++) {
                                recalculatedSeatCount[j] = guestsPerRemainingRoom;
                              }
                            }
                          }
                        }
                      }

                      return {
                        ...d,
                        occupancy: updatedOccupancy,
                        seating: updatedSeating,
                        seatingDescription: updatedSeatingDescription,
                        seatCount: recalculatedSeatCount,
                        // If dayShouldBeManuallyHandled is true, it remains/becomes true.
                        // If it was false (automatic), it remains false.
                        seatCountsManuallyEdited: dayShouldBeManuallyHandled,
                      };
                    }
                    return d;
                  }),
                });
              }}
            >
              <Icon name="trash" />
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ flexGrow: 1 }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                {t('widget:timeLabels.from')}
              </label>
              <TimeField
                value={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index]).from}
                onChange={value => patchRoomOccupancy(index, { from: value })}
              />
            </div>
            <div style={{ flexGrow: 1 }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                {t('widget:timeLabels.to')}
              </label>
              <TimeField
                value={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index]).to}
                onChange={value => patchRoomOccupancy(index, { to: value })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                {t('widget:labels.seating')}
              </label>
              <Dropdown
                popupPosition="top"
                shadowRoot={shadowRoot}
                value={formState.days[meetingRoomsDay].seating[index]}
                onChange={value => {
                  const currentText = formState.days[meetingRoomsDay].seatingDescription?.[index] || '';
                  requestFormStateUpdateUserInitiated({
                    ...formState,
                    days: formState.days.map((d, i) => {
                      if (i !== meetingRoomsDay) return d;
                      const next = applyRoomSeating(d, index, value);
                      return {
                        ...next,
                        seatCount: d.seatCount.map((sc, j) => (j === index ? 10 : sc)),
                      };
                    }),
                  });
                  if (value === OTHER_SEATING && !currentText.trim()) setOtherEditorRoom(index);
                }}
                options={seatingOptions}
              />
            </div>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                {t('widget:labels.participants')}
              </label>
              <NumField
                value={formState.days[meetingRoomsDay].seatCount[index]}
                min={1}
                onChange={value =>
                  requestFormStateUpdateUserInitiated({
                    ...formState,
                    days: formState.days.map((d, i) => {
                      if (i === meetingRoomsDay) {
                        const newSeatCount = [...d.seatCount];
                        newSeatCount[index] = value;
                        return {
                          ...d,
                          seatCount: newSeatCount,
                          seatCountsManuallyEdited: true,
                        };
                      }
                      return d;
                    }),
                  })
                }
              />
            </div>
          </div>
          {formState.days[meetingRoomsDay].seating[index] === OTHER_SEATING && (
            <button
              type="button"
              onClick={() => setOtherEditorRoom(index)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '10px 12px',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{t('widget:otherSeating.describe')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  color: (formState.days[meetingRoomsDay].seatingDescription?.[index] || '').trim() ? '#111827' : '#9ca3af',
                }}>
                  {(formState.days[meetingRoomsDay].seatingDescription?.[index] || '').trim() || t('widget:otherSeating.placeholder')}
                </span>
                <span style={{ color: '#AC2A6E', fontSize: 14, flexShrink: 0 }}>{t('widget:otherSeating.edit')}</span>
              </div>
            </button>
          )}
          <RoomRequirements
            roomKey={`${meetingRoomsDay}-${index}`}
            occupancy={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index])}
            open={!!openRequirements[`${meetingRoomsDay}-${index}`]}
            onToggle={() =>
              setOpenRequirements(current => ({ ...current, [`${meetingRoomsDay}-${index}`]: !current[`${meetingRoomsDay}-${index}`] }))
            }
            onChange={patch => patchRoomOccupancy(index, patch)}
            locale={i18n.language || 'de'}
            t={t}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <TechCounter
              icon="meeting_room"
              label={t('widget:roomEquipment.beamer')}
              value={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index]).beamerCount || 0}
              onChange={value => patchRoomOccupancy(index, { beamerCount: value })}
            />
            <TechCounter
              icon="flipchart"
              label={t('widget:roomEquipment.flipchart')}
              value={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index]).flipchartCount || 0}
              onChange={value => patchRoomOccupancy(index, { flipchartCount: value })}
            />
            <TechCounter
              icon="information"
              label={t('widget:roomEquipment.pinboard')}
              value={normalizeRoomOccupancy(formState.days[meetingRoomsDay].occupancy[index]).pinboardCount || 0}
              onChange={value => patchRoomOccupancy(index, { pinboardCount: value })}
            />
          </div>
        </div>
      ))}
      <br />
      <div style={{ padding: 15 }}>
        <LinkButton
          onClick={() => {
            const isAnyDayManuallyEditedGlobal = formState.days.some(d => d.seatCountsManuallyEdited === true);

            requestFormStateUpdateUserInitiated({
              ...formState,
              days: formState.days.map((d, i) => {
                if (i === meetingRoomsDay) {
                  const currentTotalGuests = d.totalGuests || 0;
                  const oldOccupancy = d.occupancy || [];
                  const oldSeatCount = d.seatCount || [];
                  const oldSeating = d.seating || [];

                  const newOccupancy = [...oldOccupancy, defaultRoomOccupancy()];
                  const newSeating = [...oldSeating, 'UFORM'];
                  const newSeatingDescription = [...(d.seatingDescription || []), ''];
                  const newTotalNumRooms = newOccupancy.length;
                  let updatedSeatCount = Array(newTotalNumRooms).fill(0);
                  let dayBecomesManuallyEdited = d.seatCountsManuallyEdited; // Preserve current state unless changed

                  if (isAnyDayManuallyEditedGlobal || d.seatCountsManuallyEdited) {
                    // Manual mode: preserve existing, add default for new room
                    updatedSeatCount = [...oldSeatCount];
                    const defaultNewRoomSeatCount =
                      newTotalNumRooms > 0 && currentTotalGuests > 0
                        ? Math.ceil(currentTotalGuests / newTotalNumRooms)
                        : currentTotalGuests > 0
                          ? currentTotalGuests
                          : 0;
                    updatedSeatCount.push(defaultNewRoomSeatCount);
                    dayBecomesManuallyEdited = true;
                  } else {
                    // Automatic mode: redistribute
                    if (currentTotalGuests > 0) {
                      updatedSeatCount[0] = currentTotalGuests;
                      if (newTotalNumRooms > 1) {
                        const guestsPerRemainingRoom = Math.ceil(currentTotalGuests / newTotalNumRooms);
                        for (let j = 1; j < newTotalNumRooms; j++) {
                          updatedSeatCount[j] = guestsPerRemainingRoom;
                        }
                      }
                    }
                    dayBecomesManuallyEdited = false; // Explicitly set to false if auto-distributing
                  }

                  return {
                    ...d,
                    occupancy: newOccupancy,
                    seating: newSeating,
                    seatingDescription: newSeatingDescription,
                    seatCount: updatedSeatCount,
                    seatCountsManuallyEdited: dayBecomesManuallyEdited,
                  };
                }
                return d;
              }),
            });
          }}
        >
          {t('widget:buttons.addRoom')}
        </LinkButton>
      </div>
      <OtherSeatingDialog
        open={otherEditorRoom !== null}
        value={otherEditorRoom === null ? '' : formState.days[meetingRoomsDay]?.seatingDescription?.[otherEditorRoom] || ''}
        onCancel={() => setOtherEditorRoom(null)}
        onSave={text => {
          if (otherEditorRoom === null) return;
          requestFormStateUpdateUserInitiated({
            ...formState,
            days: formState.days.map((day, dayIndex) => (
              dayIndex === meetingRoomsDay ? applyRoomSeatingDescription(day, otherEditorRoom, text) : day
            )),
          });
          setOtherEditorRoom(null);
        }}
      />
    </>
  );
};

const TechCounter = ({
  icon,
  label,
  value,
  onChange,
}: {
  icon: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
      <span style={{ width: 16, height: 16, display: 'inline-flex', flex: '0 0 16px' }}>
        <span style={{ transform: 'scale(0.67)', transformOrigin: 'top left' }}>
          <Icon name={icon} />
        </span>
      </span>
      {label}
    </span>
    <NumField value={value} min={0} max={TECH_COUNT_MAX} onChange={onChange} />
  </div>
);

/**
 * Collapsed room-requirement summary. Only selected values appear in parentheses.
 */
const RoomRequirements = ({
  roomKey,
  occupancy,
  open,
  onToggle,
  onChange,
  locale,
  t,
}: {
  roomKey: string;
  occupancy: Occupancy;
  open: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<Occupancy>) => void;
  locale: string;
  t: (key: string) => string;
}) => {
  const summary = formatRoomRequirementsSummary(
    occupancy,
    {
      airConditioning: t('widget:roomRequirements.airConditioning'),
      daylight: t('widget:roomRequirements.daylight'),
    },
    locale,
  );
  const title = summary ? `${t('widget:roomRequirements.title')} (${summary})` : t('widget:roomRequirements.title');
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 13,
        }}
      >
        <span>{title}</span>
        <Icon name={open ? 'chevronup' : 'chevrondown'} />
      </button>
      {open && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Checkbox
              name={`air-${roomKey}`}
              label={t('widget:roomRequirements.airConditioning')}
              checked={!!occupancy.requireAirConditioning}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ requireAirConditioning: event.target.checked })}
            />
            <Checkbox
              name={`daylight-${roomKey}`}
              label={t('widget:roomRequirements.daylight')}
              checked={!!occupancy.requireDaylight}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ requireDaylight: event.target.checked })}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{t('widget:roomRequirements.minSize')}</div>
            <MinAreaField
              value={occupancy.minRoomAreaSqm || 0}
              placeholder={t('widget:roomRequirements.noMinimum')}
              onChange={minRoomAreaSqm => onChange({ minRoomAreaSqm })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Whole-number room size. Empty and 0 mean no minimum. Decimal input keeps the integer part.
 */
const MinAreaField = ({ value, placeholder, onChange }: { value: number; placeholder: string; onChange: (value: number) => void }) => {
  const [text, setText] = useState(value > 0 ? String(value) : '');

  useEffect(() => {
    setText(value > 0 ? String(value) : '');
  }, [value]);

  const apply = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setText('');
      onChange(0);
      return;
    }
    const head = trimmed.split(/[.,]/)[0].replace(/[^\d]/g, '');
    if (!head) return;
    const next = Math.min(MIN_ROOM_AREA_SQM_MAX, parseInt(head, 10));
    setText(next > 0 ? String(next) : '');
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        inputMode="numeric"
        value={text}
        placeholder={placeholder}
        onChange={event => apply(event.target.value)}
        onPaste={event => {
          event.preventDefault();
          apply(event.clipboardData.getData('text'));
        }}
        style={{ width: 150, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#f3f6fb' }}
      />
      <span style={{ fontSize: 13, color: '#666' }}>m²</span>
    </div>
  );
};

export default MeetingRoomsSectionDrawer;
